import { FC, useEffect, useState } from 'react';
import { observer } from 'mobx-react';

import { DateTimeUtils } from '@servicetitan/datetime-utils';
import {
    Banner,
    BodyText,
    Button,
    Card,
    Dialog,
    Form,
    Headline,
    Layout,
    Page,
    Tooltip,
} from '@servicetitan/design-system';
import { provide, useDependencies } from '@servicetitan/react-ioc';

import { PageSpinner } from '../../common';
import { ApiUsageReportPageStore } from '../stores/api-usage-report-page.store';
import { AppPageHeader } from './app-page-header';

export interface ApiUsageReportPageProps {
    id: number;
}

export const ApiUsageReportPage: FC<ApiUsageReportPageProps> = provide({
    singletons: [ApiUsageReportPageStore],
})(
    observer(({ id }) => {
        const [store, dateTimeUtils] = useDependencies(ApiUsageReportPageStore, DateTimeUtils);
        const [noDataDialogVisible, setNoDataDialogVisible] = useState(false);

        useEffect(() => {
            store.load(id);
        }, [store, id]);

        if (store.loading) {
            return <PageSpinner />;
        }

        const pageHeader = (
            <AppPageHeader
                id={id}
                publicId={store.publicId}
                appName={store.appName}
                deleted={store.deleted}
                errorBannerVisible={store.errorBannerVisible}
            />
        );

        if (store.errorBannerVisible) {
            return (
                <Page header={pageHeader}>
                    <Layout>
                        <Banner
                            status="critical"
                            icon
                            title="An error occurred while loading this page."
                        />
                    </Layout>
                </Page>
            );
        }

        return (
            <Page header={pageHeader}>
                <Layout style={{ maxWidth: '48em' }}>
                    <Layout.Section>
                        <Headline>API Usage Report (Production Environment)</Headline>
                        <BodyText>
                            This report lists the number of{' '}
                            <Tooltip text="2xx HTTP response status code" underline>
                                successful
                            </Tooltip>{' '}
                            API calls made by your app during a specified date range.
                        </BodyText>
                        <BodyText className="m-t-1">
                            Your app was registered on{' '}
                            {dateTimeUtils.formatDateFull(store.createdOn!)}. You may specify any
                            date range between {dateTimeUtils.formatDateFull(store.minDate!)} and
                            today, but note that data from the past 24 hours might not be fully
                            available yet.
                        </BodyText>
                        <Form className="m-t-3">
                            <Card>
                                <Form.DateRangePicker
                                    label="Date Range (Pacific Time)"
                                    labelProps={{ required: true }}
                                    dropdown={{ portal: false }}
                                    minDate={store.minDate}
                                    maxDate={new Date()}
                                    value={store.dateRange}
                                    onChange={(dateRange: {
                                        start?: Date | null;
                                        end?: Date | null;
                                    }) => {
                                        store.dateRange = dateRange;
                                    }}
                                />
                                <Button
                                    primary
                                    disabled={!store.dateRange.start || !store.dateRange.end}
                                    loading={store.downloading}
                                    onClick={async () => {
                                        if (!(await store.downloadReport())) {
                                            setNoDataDialogVisible(true);
                                        }
                                    }}
                                >
                                    Download CSV File
                                </Button>
                            </Card>
                        </Form>
                    </Layout.Section>
                    <Dialog
                        title="No Data Available"
                        primaryActionName="Close"
                        onPrimaryActionClick={() => {
                            setNoDataDialogVisible(false);
                        }}
                        onClose={() => {
                            setNoDataDialogVisible(false);
                        }}
                        open={noDataDialogVisible}
                    >
                        <BodyText>
                            There were no successful API calls during the specified date range.
                        </BodyText>
                        {(store.dateRange.end?.getTime() ?? 0) + 24 * 60 * 60 * 1000 >
                            Date.now() && (
                            <BodyText className="m-t-1">
                                Note that data from the past 24 hours might not be fully available
                                yet.
                            </BodyText>
                        )}
                    </Dialog>
                </Layout>
            </Page>
        );
    }),
);
ApiUsageReportPage.displayName = 'ApiUsageReportPage';
